import { useState, useEffect } from 'react';
import { UserPlus, Shield, User, Mail, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { useTheme } from '@/context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
const API = `${BACKEND_URL}/api`;
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const Users = () => {
  const { isDark } = useTheme();
  const [filter, setFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Map API users to match the expected format
  const mappedUsers = users.map(user => ({
    id: user.id,
    name: user.name,
    email: user.email,
    role: user.role,
    status: 'Active', // Users are active by default
    joinedAt: user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB') : 'N/A',
    projects: 0, // Will be calculated separately if needed
  }));

  const filteredUsers =
    filter === 'all' ? mappedUsers : mappedUsers.filter(u => u.role?.toLowerCase() === filter.toLowerCase());

  const getRoleColor = (role) => {
    if (isDark) {
      switch (role) {
        case 'Owner':
          return 'bg-purple-500/30 text-purple-200';
        case 'Admin':
          return 'bg-red-500/30 text-red-200';
        case 'Founder':
          return 'bg-blue-500/30 text-blue-200';
        case 'Student':
          return 'bg-green-500/30 text-green-200';
        default:
          return 'bg-gray-500/30 text-gray-300';
      }
    } else {
      switch (role) {
        case 'Owner':
          return 'bg-purple-100 text-purple-700';
        case 'Admin':
          return 'bg-red-100 text-red-700';
        case 'Founder':
          return 'bg-blue-100 text-blue-700';
        case 'Student':
          return 'bg-green-100 text-green-700';
        default:
          return 'bg-gray-100 text-gray-700';
      }
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-2 transition-colors ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Users</h2>
          <p className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Manage users and role-based access</p>
        </div>
        <div className="w-full sm:w-auto">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className={`w-full sm:w-40 ${
              isDark 
                ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/30 text-purple-200' 
                : 'bg-white border-gray-300'
            }`}>
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/30' : ''}>
              <SelectItem value="all" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>All Users</SelectItem>
              <SelectItem value="student" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>Students</SelectItem>
              <SelectItem value="founder" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>Founders</SelectItem>
              <SelectItem value="admin" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>Admins</SelectItem>
              <SelectItem value="owner" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>Owners</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Users</p>
          <p className={`text-2xl font-bold transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>{mappedUsers.length}</p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Students</p>
          <p className="text-2xl font-bold text-green-600">
            {mappedUsers.filter(u => u.role === 'Student').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Founders</p>
          <p className="text-2xl font-bold text-blue-600">
            {mappedUsers.filter(u => u.role === 'Founder').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Admins</p>
          <p className="text-2xl font-bold text-red-600">
            {mappedUsers.filter(u => u.role === 'Admin' || u.role === 'Owner').length}
          </p>
        </div>
      </div>

      {/* Users - Desktop Table / Mobile Cards */}
      {loading ? (
        <div className={`rounded-xl shadow-md border p-8 sm:p-12 text-center transition-colors ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading users...</p>
        </div>
      ) : (
        <div className={`rounded-xl shadow-md border overflow-hidden transition-colors ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50'}>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>ID</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>NAME</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>EMAIL</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>ROLE</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>PROJECTS</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>STATUS</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>JOINED AT</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className={`border-b transition-colors group ${
                      isDark 
                        ? 'border-purple-500/20 hover:bg-purple-500/10' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <TableCell className={`font-medium transition-colors ${
                      isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                    }`}>{user.id}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {(user.role === 'Admin' || user.role === 'Owner') && (
                          <Shield className={`w-4 h-4 transition-colors ${
                            isDark ? 'text-purple-400 group-hover:text-purple-300' : 'text-purple-600'
                          }`} />
                        )}
                        <span className={`font-medium transition-colors ${
                          isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                        }`}>{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{user.email}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    </TableCell>
                    <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{user.projects}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.status === 'Active'
                            ? isDark
                              ? 'bg-green-500/30 text-green-200'
                              : 'bg-green-100 text-green-700'
                            : isDark
                              ? 'bg-gray-500/30 text-gray-300'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {user.status}
                      </span>
                    </TableCell>
                    <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{user.joinedAt}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                          isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                        }`}>
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className={`h-8 px-3 ${
                          isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                        }`}>
                          Edit Role
                        </Button>
                      </div>
                    </TableCell>
                  </tr>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-purple-500/20">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className={`p-4 transition-colors ${
                  isDark 
                    ? 'hover:bg-purple-500/10' 
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      {(user.role === 'Admin' || user.role === 'Owner') && (
                        <Shield className={`w-4 h-4 transition-colors ${
                          isDark ? 'text-purple-400' : 'text-purple-600'
                        }`} />
                      )}
                      <span className={`text-xs font-semibold transition-colors ${
                        isDark ? 'text-purple-300' : 'text-purple-600'
                      }`}>{user.id.substring(0, 8)}...</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.status === 'Active'
                            ? isDark
                              ? 'bg-green-500/30 text-green-200'
                              : 'bg-green-100 text-green-700'
                            : isDark
                              ? 'bg-gray-500/30 text-gray-300'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {user.status}
                      </span>
                    </div>
                    <h3 className={`font-semibold mb-1 transition-colors ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>{user.name}</h3>
                    <p className={`text-xs mb-2 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>{user.email}</p>
                  </div>
                  <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                    <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                      isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                    }`}>
                      <Mail className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className={`h-7 px-3 text-xs ${
                      isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                    }`}>
                      Edit Role
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className={`text-xs mb-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Projects</p>
                    <p className={`font-medium transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{user.projects}</p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Joined At</p>
                    <p className={`font-medium transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{user.joinedAt}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;

